import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LoginForm } from '@/components/auth/LoginForm';
import { CreditoDesenvolvedor } from '@/components/auth/CreditoDesenvolvedor';
import { useAuth } from '@/contexts/auth-hook';
import { useProjeto } from '@/contexts/projeto-hook';
import { useI18n } from '@/contexts/i18n-hook';
import { AUTH_RELOGIN_MESSAGE_KEY, hasStoredProject, resolveAuthenticatedEntryRoute } from '@/lib/session-utils';

export function Login() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { login, isAuthenticated, loading, isAdmin, user } = useAuth();
  const { carregarProjetos, projeto } = useProjeto();
  const [error, setError] = useState<string | undefined>(undefined);

  useEffect(() => {
    const reloginMessage = sessionStorage.getItem(AUTH_RELOGIN_MESSAGE_KEY);
    if (reloginMessage) {
      setError(reloginMessage);
      sessionStorage.removeItem(AUTH_RELOGIN_MESSAGE_KEY);
    }
  }, []);

  useEffect(() => {
    if (loading || !isAuthenticated) return;
    // Redirecionar clientes para o portal
    if (user?.role === 'cliente') {
      navigate('/portal', { replace: true });
      return;
    }
    const projectAvailable = Boolean(projeto) || hasStoredProject();
    navigate(resolveAuthenticatedEntryRoute(projectAvailable, isAdmin), { replace: true });
  }, [loading, isAuthenticated, isAdmin, user, projeto, navigate]);

  const handleSubmit = async (data: { email: string; senha: string }) => {
    setError(undefined);
    const result = await login(data.email, data.senha);
    if (result.success) {
      // Redirecionar clientes para o portal
      if (result.user?.role === 'cliente') {
        navigate('/portal', { replace: true });
        return;
      }

      try {
        await carregarProjetos();
      } catch (error) {
        console.error('Falha ao carregar projetos após login:', error);
      }

      const projectAvailable = Boolean(projeto) || hasStoredProject();
      navigate(resolveAuthenticatedEntryRoute(projectAvailable, isAdmin), { replace: true });
    } else {
      setError(result.message || t('auth.login.erroCredenciais'));
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-white lg:flex-row">
      {/* ESQUERDA — marca. Só no desktop; no mobile vira o cabeçalho do formulário. */}
      <aside className="relative hidden overflow-hidden lg:flex lg:w-1/2 lg:flex-col lg:justify-between lg:p-14 xl:p-20">
        {/* Gradiente: mais claro em cima, onde fica a marca, para o "Guard" escuro ler bem */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-[linear-gradient(150deg,#0A4A96_0%,#00377B_45%,#002A4A_100%)]"
        />
        {/* Malha técnica, referência a desenho de engenharia */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-[0.13] [background-image:linear-gradient(rgba(255,255,255,.55)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.55)_1px,transparent_1px)] [background-size:46px_46px]"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-32 -top-32 h-[28rem] w-[28rem] rounded-full bg-schmersal-turquoise/25 blur-3xl"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -bottom-40 -left-24 h-96 w-96 rounded-full bg-schmersal-turquoise/10 blur-3xl"
        />
        {/* Vinheta, para o conteúdo não competir com o brilho */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_90%_at_50%_0%,transparent_35%,rgba(0,26,52,0.45)_100%)]"
        />

        <div className="relative">
          <span className="text-[46px] font-bold leading-none tracking-tight xl:text-[52px]">
            <span className="text-white">Risk</span>
            <span className="text-[#00234A]">Guard</span>
          </span>
        </div>

        <div className="relative max-w-lg">
          <div className="h-1.5 w-20 rounded-full bg-schmersal-turquoise" />
          <p className="mt-8 text-[28px] font-light leading-snug text-white xl:text-[32px]">
            {t('auth.login.marca.chamada')}
          </p>
          <p className="mt-6 text-[15px] leading-relaxed text-white/55">
            {t('auth.login.marca.apoio')}
          </p>
        </div>

        <div className="relative">
          <CreditoDesenvolvedor variante="claro" />
        </div>
      </aside>

      {/* DIREITA — apenas o login */}
      <main className="relative flex flex-1 items-center justify-center bg-[linear-gradient(180deg,#FFFFFF_0%,#F4F7FA_100%)] px-5 py-12 sm:px-8 lg:px-12">
        <div className="w-full max-w-[440px] rounded-2xl border border-schmersal-gray/70 bg-white p-7 shadow-[0_1px_2px_rgba(0,42,74,0.04),0_16px_40px_-12px_rgba(0,42,74,0.14)] sm:p-10">
          {/* No mobile o painel de marca some, então a marca aparece aqui */}
          <div className="mb-9 lg:hidden">
            <span className="text-[30px] font-bold leading-none tracking-tight">
              <span className="text-schmersal-blue">Risk</span>
              <span className="text-schmersal-blue-dark">Guard</span>
            </span>
          </div>

          <h1 className="text-[26px] font-semibold tracking-tight text-schmersal-gray-dark sm:text-3xl">
            {t('auth.login.titulo')}
          </h1>
          <p className="mt-2 text-sm text-schmersal-gray-medium">
            {t('auth.login.subtitulo')}
          </p>

          <div className="mt-8">
            <LoginForm onSubmit={handleSubmit} error={error} />
          </div>

        </div>

        {/* No mobile o crédito fica fora do card, rodapé da tela */}
        <div className="absolute bottom-6 left-0 right-0 flex justify-center lg:hidden">
          <CreditoDesenvolvedor />
        </div>
      </main>
    </div>
  );
}
